import { Controller } from '@hotwired/stimulus'
import { pushPath, resetIfNoBackTarget, dropStaleEntries } from '../utils/navigation_history'

// Remembers state about the page being left, right before Turbo navigates away from it:
// - scroll position, restored when returning to it via BackLink/CancelLink (see PENDING_KEY)
// - the navigation trail used by BackLinkComponent (back_controller.js) instead of document.referrer
// Both piggyback on the same click-capture/turbo:load lifecycle, hence one controller for both.
const POSITIONS_KEY = 'scrollPositions'
const PENDING_KEY = 'scrollPendingRestore'
const LAST_LOADED_KEY = 'navMemoryLastLoadedPath'
const MAX_ENTRIES = 3
const MOBILE_MAX_WIDTH = 768

export default class extends Controller {
  connect() {
    history.scrollRestoration = 'manual'

    // Store the initial path so _onLoad does not trigger scroll-to-top on the very first page load
    // (turbo:load also fires on cold start).
    this._lastLoadedPath = sessionStorage.getItem(LAST_LOADED_KEY) ?? window.location.pathname

    // Bind once — the same reference must be passed to removeEventListener.
    this._captureHandler = this._onCapture.bind(this)
    this._loadHandler = this._onLoad.bind(this)
    this._beforeRenderHandler = this._onBeforeRender.bind(this)

    // Capture phase: fires BEFORE bubble phase, i.e. before scroll_window_top_controller
    // which may reset scrollY to 0 in bubble phase.
    document.addEventListener('click', this._captureHandler, { capture: true })
    document.addEventListener('turbo:load', this._loadHandler)
    // Navigation happens inside layout_turbo_frame, not as a full-page render.
    document.addEventListener('turbo:before-frame-render', this._beforeRenderHandler)
  }

  disconnect() {
    document.removeEventListener('click', this._captureHandler, { capture: true })
    document.removeEventListener('turbo:load', this._loadHandler)
    document.removeEventListener('turbo:before-frame-render', this._beforeRenderHandler)
  }

  _onCapture(event) {
    const link = event.target.closest('a[href]')
    if (!link) return

    const pathChanged = new URL(link.href).pathname !== window.location.pathname

    if ('scrollNoSave' in link.dataset) {
      // Return link (BackLink or CancelLink) — do not save position, set the restore flag instead.
      // BackLink is skipped here: back_controller#navigate may change href in bubble phase
      // (after our capture), so the final URL would be unknown at this point.
      // BackLink sets PENDING_KEY itself via _markPendingRestore() after navigate() completes.
      const isBackLink = (link.dataset.controller || '').split(/\s+/).includes('back')
      if (!isBackLink) {
        sessionStorage.setItem(PENDING_KEY, new URL(link.href).pathname)
      }
    } else {
      // Regular click — clear any stale restore flag.
      sessionStorage.removeItem(PENDING_KEY)

      // Save position only on pathname change — pagination changes query params but not pathname,
      // so we don't overwrite the saved list position.
      if (pathChanged) {
        this._savePosition(window.location.pathname, window.scrollY)
      }
    }

    // Don't record leaving via a Back/Cancel link (data-back-navigable) itself — that's a backward
    // move, not a step worth returning to later, and would otherwise loop between the two pages.
    if (pathChanged && !('backNavigable' in link.dataset)) {
      // Full path incl. query string — e.g. active filters on an index page — so BackLink restores them too.
      pushPath(window.location.pathname + window.location.search)
      // Remember what we just left, so a turbo:load that's still in flight for THIS page (delayed by
      // e.g. CPU pressure) can be told apart from the one for wherever we're headed next — see _onLoad.
      this._navigatingAwayFrom = window.location.pathname
    }
  }

  _onBeforeRender(event) {
    // Only intercept the main layout frame — modal and other frames are unaffected.
    if (event.target.id === 'layout_turbo_frame' || event.target.id === 'main_content_turbo_frame') {
      document.documentElement.classList.add('turbo-loading')
    }
  }

  _onLoad() {
    const currentPath = window.location.pathname
    // Guard against pagination and other navigation without a pathname change (e.g. promoted frame with ?page=2).
    if (currentPath === this._lastLoadedPath) {
      this._revealContainer()
      return
    }
    // Late turbo:load from a navigation we've already clicked past, e.g.:
    //   _onCapture(A) -> _onCapture(B) -> _onLoad(A, late) -> _onLoad(B)
    // currentPath still reads as A here — skip, _onLoad(B) will run the real cleanup.
    if (currentPath === this._navigatingAwayFrom) {
      this._revealContainer()
      return
    }
    this._navigatingAwayFrom = null
    this._lastLoadedPath = currentPath
    sessionStorage.setItem(LAST_LOADED_KEY, currentPath)
    resetIfNoBackTarget()
    // A page reached via the browser's own back/forward (swipe, gesture, history menu) never goes
    // through _onCapture/popStack, so a stale entry pointing at the page we just landed on can
    // survive natively-driven navigation just like it does after a form submit — same cleanup here.
    dropStaleEntries(currentPath)

    // If a restore flag is set and matches the current path — restore scroll.
    const pendingPath = sessionStorage.getItem(PENDING_KEY)
    if (pendingPath === currentPath) {
      sessionStorage.removeItem(PENDING_KEY)
      const entry = this._getPositions().find((e) => e.path === currentPath)
      if (entry) {
        this._revealContainer(entry.y)
        return
      }
    }

    // Default mobile behavior: every new page starts at Y=0.
    const scrollTarget = window.innerWidth < MOBILE_MAX_WIDTH ? 0 : null
    this._revealContainer(scrollTarget)
  }

  _revealContainer(scrollTop = null) {
    requestAnimationFrame(() => {
      if (scrollTop !== null) window.scrollTo({ top: scrollTop, behavior: 'instant' })
      document.documentElement.classList.remove('turbo-loading')
    })
  }

  _savePosition(path, y) {
    // Remove any existing entry for this path and prepend the new one (LRU, max 3 paths).
    let positions = this._getPositions().filter((e) => e.path !== path)
    positions.unshift({ path, y })
    sessionStorage.setItem(POSITIONS_KEY, JSON.stringify(positions.slice(0, MAX_ENTRIES)))
  }

  _getPositions() {
    try {
      return JSON.parse(sessionStorage.getItem(POSITIONS_KEY) || '[]')
    } catch {
      return []
    }
  }
}
